import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/reports';

interface ChartDataProperties {
  label: string;
  scheduledPowerConsumed: number;
  guestPowerConsumed: number;
  [key: string]: string | number; // Add this to allow dynamic properties
}

/**
 * Energy report duration chart
 */
function EnergyReportDurationChart() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });

  /**
   * Custom tooltip
   * @param {any} properties - The properties
   * @returns {any} The tooltip
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;

      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>
              {t('scheduleCharging')}: {Number(data?.scheduledPowerConsumed || 0).toFixed(2)} kWh
            </p>
            <p style={{ color: '#2AA700' }}>
              {t('guestCharging')}: {Number(data?.guestPowerConsumed || 0).toFixed(2)} kWh
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The time period for which to fetch the report.
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'powerConsumedByGuestVsSchedule',
        accountCustomId: reportsData?.accountId,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];

        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(response.data).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          default:
            transformedData = [];
        }

        setBarGraphData(transformedData);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * If clicking the already active pin, deactivate it
   * @param {any} name - The name
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('powerConsumedByGuestVsSchedule') ? 'active' : ''}`}
              onClick={() => handleClick('powerConsumedByGuestVsSchedule')}>
              {reportsData.activePinName.includes('powerConsumedByGuestVsSchedule') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('energyConsumptionGuestScheduled')}{' '}
          </div>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(start, end) => {
              setDateRange({ startDate: start, endDate: end });
              fetchPowerConsumedReport('last24hours');
            }}
            placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          />
        </div>

        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData} barCategoryGap={25}>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                fontSize={12}
                stroke="#535353"
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar dataKey="scheduledPowerConsumed" stackId="a" barSize={26} fill="#4ABDFB" />
              <Bar dataKey="guestPowerConsumed" stackId="a" barSize={26} fill="#2AA700" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="energy-report-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#4ABDFB' }} />
          <p>{t('scheduleCharging')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
          <p>{t('guestCharging')}</p>
        </div>
      </div>
    </div>
  );
}

export default EnergyReportDurationChart;
